import React, { Component } from 'react';
import {BrowserRouter, Route, Link, Switch } from 'react-router-dom';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Redirect } from "react-router-dom";
import './App.css';
import NslMailAPI from './NslMailAPI.js';
import {SessionContext} from './components/SessionContext.js';
import HomeView from './components/HomeView.js';
import AccountSettings from './components/AccountSettings.js';
import AliasList from './components/AliasList.js';
import AliasView from './components/AliasView.js';
import NewAlias from './components/NewAlias.js';
import Bookmarklet from './components/Bookmarklet.js';
import PopupLookup from './components/PopupLookup';
import Faq from './components/Faq.js';
import ForgotPassword from './components/ForgotPassword.js';
import ResetPassword from './components/ResetPassword.js';
import Privacy from './components/Privacy.js';
import Contact from './components/Contact.js';
import SignupForm from './components/SignupForm.js';
import VerifyEmail from './components/VerifyEmail.js';
import SimpleMailViewer from './components/SimpleMailViewer.js';
import {Section} from './Common.js';

// Mail page layout component with compact top section and spacious inbox
class MailPageLayout extends Component {
  constructor() {
    super();
    this.state = {
      showInstructions: false
    };
  }

  toggleInstructions = () => {
    this.setState(prevState => ({
      showInstructions: !prevState.showInstructions
    }));
  };

  render() {
    const { showInstructions } = this.state;

    return (
      <div className="mail-page-layout">
        {/* Compact top section with buttons */}
        <div className="mail-page-header">
          <div className="mail-header-buttons">
            <button 
              onClick={this.toggleInstructions} 
              className="btn btn-ghost btn-sm"
            >
              {showInstructions ? 'Hide Instructions' : 'Email Client Setup Instructions'}
            </button>
            <a href="/webmail/" target="_blank" className="btn btn-ghost btn-sm">
              Legacy Webmail
            </a>
          </div>
        </div>

        {/* Collapsible instructions section */}
        {showInstructions && (
          <Section name="Email Client Setup">
            <p>You can access your NslMail messages using any POP3/IMAP-compatible email client such as Outlook, Thunderbird, or Apple Mail. SSL encryption is available for secure connections.</p>
            <p><strong>Preferred usage:</strong> For most users, the best way to use NslMail is to set up POP3 fetching in another email service, such as Gmail. This allows you to read and reply to your NslMail messages directly from your main inbox. In Gmail, go to Settings &gt; Accounts and Import &gt; Check mail from other accounts, and add your NslMail account using the settings below.</p>
            
            <p><strong>Incoming Mail Settings (POP3 - Recommended):</strong></p>
            <ul>
              <li><strong>Server:</strong> pop.nslmail.com</li>
              <li><strong>Port:</strong> 995 (SSL) or 110 (standard)</li>
              <li><strong>Security:</strong> SSL/TLS (recommended) or None</li>
              <li><strong>Username:</strong> Your NslMail account username</li>
              <li><strong>Password:</strong> Your NslMail account password</li>
            </ul>

            <p><strong>Incoming Mail Settings (IMAP - Alternative):</strong></p>
            <ul>
              <li><strong>Server:</strong> imap.nslmail.com</li>
              <li><strong>Port:</strong> 993 (SSL) or 143 (standard)</li>
              <li><strong>Security:</strong> SSL/TLS (recommended) or None</li>
              <li><strong>Username:</strong> Your NslMail account username</li>
              <li><strong>Password:</strong> Your NslMail account password</li>
            </ul>

            <p>Use the same username and password that you use to log into this website. Most modern email clients will automatically detect the correct settings when you enter the server addresses above. For the best security, always choose SSL/TLS or STARTTLS encryption when available.</p>
          </Section>
        )}

        {/* Spacious inbox section */}
        <Section name="Inbox">
          <SimpleMailViewer />
        </Section>
      </div>
    );
  }
}

class App extends Component {

  constructor() {
    super();

    this.messageCountTimer = null;
    this.tokenRefreshWorker = null;
    this.idleTimer = null;
    this.lastActivityTime = Date.now();
    this.isIdle = false;
    this.IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

    this.state = this.initialState = {
      isReady: false,
      isLoggedIn: false,
      accessToken: null,
      user: null,
      newMessageCount: 0,
      aliasesHeldCount: 0,
      refreshCounter: 0,
      actions: {
        reloadUser: this.loadUserProfile,
        setAccessToken: this.setAccessToken,
        showLoading: this.showLoading,
        logout: this.logout,
        loadAliasesHeldCount: this.loadAliasesHeldCount,
        loadMessageCount: this.loadMessageCount,
        refreshData: this.refreshData,
        getAPI: () => this.API
      }
    }
  }

  reloadUser = () => {
    console.log("fn: Reload the user!!");
  }

  logout = () => {
    console.log("fn: Logout");
    this.clearMessageCountTimer();
    this.stopTokenRefreshWorker();
    this.clearIdleTimer();
    this.setAccessToken(null);
    this.setState({redirect: '/'});
  }

  loadUserProfile = async() => {
    console.log("fn: load user profile");

    let user = await this.API.getUserProfile();
    console.log("User Profile", user);
    if (user) {
      this.setState({user: user});
      await this.loadMessageCount();
      await this.loadAliasesHeldCount();
      this.startMessageCountTimer();
      this.startTokenRefreshWorker(this.state.accessToken);
    }
  }

  loadMessageCount = async() => {
    if (!this.API) return;
    
    try {
      let count = await this.API.getNewMessageCount();
      this.setState({newMessageCount: count});
    } catch (e) {
      console.log("Error loading message count:", e);
    }
  }

  loadAliasesHeldCount = async() => {
    if (!this.API) return;
    
    try {
      let count = await this.API.getAliasesHeldCount();
      this.setState({aliasesHeldCount: count});
    } catch (e) {
      console.log("Error loading aliases held count:", e);
      console.log("Error details:", e.message);
    }
  }

  startMessageCountTimer = () => {
    this.clearMessageCountTimer();
    this.messageCountTimer = setInterval(() => {
      this.loadMessageCount();
    }, 60000); // 60 seconds
  }

  clearMessageCountTimer = () => {
    if (this.messageCountTimer) {
      clearInterval(this.messageCountTimer);
      this.messageCountTimer = null;
    }
  }

  // Start Web Worker for background token refresh (not throttled by browser)
  startTokenRefreshWorker = (token) => {
    this.stopTokenRefreshWorker();

    try {
      this.tokenRefreshWorker = new Worker('/tokenRefreshWorker.js');

      this.tokenRefreshWorker.onmessage = (e) => {
        const { type, token: newToken, status, tokenAgeHours } = e.data;

        switch (type) {
          case 'TOKEN_REFRESHED':
            console.log("Worker refreshed token successfully");
            localStorage.setItem('accessToken', newToken);
            this.setState({ accessToken: newToken });
            if (this.API) {
              this.API.accessToken = newToken;
            }
            break;

          case 'TOKEN_REFRESH_FAILED':
            console.error(`Worker token refresh failed: ${status}, token was ${tokenAgeHours} hours old`);
            // The status is the server's verdict on the token we just presented,
            // so it decides this rather than our own reading of the token's age:
            // 401 is expired or invalid, 403 is an account no longer approved.
            // Anything else is the server having a bad day and keeps the session.
            if (status === 401 || status === 403) {
              console.log("Token rejected, logging out");
              this.logout();
            }
            break;

          case 'TOKEN_REFRESH_ERROR':
            console.error("Worker token refresh error:", e.data.error);
            break;

          default:
            break;
        }
      };

      this.tokenRefreshWorker.onerror = (error) => {
        console.error("Token refresh worker error:", error);
      };

      // Initialize the worker with current token and API URL
      this.tokenRefreshWorker.postMessage({
        type: 'INIT',
        token: token,
        apiUrl: process.env.REACT_APP_API_URL
      });

      console.log("Token refresh worker started");
    } catch (e) {
      console.error("Failed to start token refresh worker:", e);
    }
  }

  stopTokenRefreshWorker = () => {
    if (this.tokenRefreshWorker) {
      this.tokenRefreshWorker.postMessage({ type: 'STOP' });
      this.tokenRefreshWorker.terminate();
      this.tokenRefreshWorker = null;
      console.log("Token refresh worker stopped");
    }
  }

  refreshData = async() => {
    console.log("fn: refreshData - refreshing all data");
    await this.loadMessageCount();
    await this.loadAliasesHeldCount();
    this.setState(prevState => ({
      refreshCounter: prevState.refreshCounter + 1
    }));
  }

  handleActivity = () => {
    this.lastActivityTime = Date.now();
    
    if (this.isIdle) {
      console.log("User returned from idle, refreshing data");
      this.isIdle = false;
      this.refreshData();
    }
    
    this.startIdleTimer();
  }

  startIdleTimer = () => {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    
    this.idleTimer = setTimeout(() => {
      console.log("User is now idle");
      this.isIdle = true;
    }, this.IDLE_TIMEOUT);
  }

  clearIdleTimer = () => {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  showLoading = async(show) => {
    this.setState({loading: show})
  }

  setAccessToken = async(accessToken) => {
    if (accessToken === "null") accessToken = null; // fix bad data from somewhere
    //console.log("setAccessToken", accessToken);
    localStorage.setItem('accessToken', accessToken);

    if (accessToken) {
      //console.log("App.setAccessToken", accessToken);
      this.API = new NslMailAPI(accessToken, this.showLoading);
      this.setState({accessToken: accessToken, loginError: null}, async () => {
        try {
          await this.loadUserProfile();
          this.setState({isLoggedIn: true, isReady: true});
          this.startIdleTimer();
        } catch (e) {
          console.log("Token Invalid");
          console.log("App.clearAccessToken");
          this.clearIdleTimer();
          this.setState(this.initialState);
          this.setState({isReady: true});
        }
      });
    } else {
      console.log("App.clearAccessToken");
      this.clearMessageCountTimer();
      this.stopTokenRefreshWorker();
      this.clearIdleTimer();
      this.setState(this.initialState);
      this.setState({isReady: true});
    }
  }

  componentDidMount() {
    let token = localStorage.getItem('accessToken');
    if (token) {
      this.setAccessToken(token);
    } else {
      this.setState({isReady: true});
    }

    // Add mouse activity listeners for auto-refresh
    document.addEventListener('mousemove', this.handleActivity);
    document.addEventListener('mousedown', this.handleActivity);
    document.addEventListener('keydown', this.handleActivity);
    document.addEventListener('scroll', this.handleActivity);
  }

  componentWillUnmount() {
    this.clearMessageCountTimer();
    this.stopTokenRefreshWorker();
    this.clearIdleTimer();

    // Remove activity listeners
    document.removeEventListener('mousemove', this.handleActivity);
    document.removeEventListener('mousedown', this.handleActivity);
    document.removeEventListener('keydown', this.handleActivity);
    document.removeEventListener('scroll', this.handleActivity);
  }

  render() {
    let secureRoutes = [];

    if (this.state.isReady) {
      if (this.state.isLoggedIn) {
        secureRoutes = [
          <Route key="account" path="/account" component={AccountSettings} />,
          <Route key="aliases" path="/aliases" component={AliasList} exact />,
          <Route key="alias-view" path="/aliases/:aliasId" component={AliasView} />,
          <Route key="new-alias" path="/new-alias" component={NewAlias} />
        ];
      } else {
        secureRoutes = [
          <Route key="account-home" path="/account" component={HomeView} />,
          <Route key="aliases-home" path="/aliases" component={HomeView} />
        ];
      }
    }

    let loadSpinner = null;
    if (this.state.loading)
      loadSpinner = <div className="loading_spinner">
        <img src="/images/indicator_big.gif" alt="loading" />
      </div>

    // Check if we're on the popup route - if so, render without header/nav/footer
    const isPopup = window.location.pathname.startsWith('/popup');
    
    let body;
    if (isPopup) {
      body = (
        <Switch>
          <Route path="/popup/lookup" component={PopupLookup} />
        </Switch>
      );
    } else {
      body = (
        <div className="App">
        <div className="header" onClick={this.state.isLoggedIn ? this.refreshData : undefined} style={{cursor: this.state.isLoggedIn ? 'pointer' : 'default'}} />
        {loadSpinner}
        <div className="navigation">
          <div className="nav-left">
            {!this.state.isLoggedIn && (
              <Link to="/" className="text-primary-600 font-medium">Home</Link>
            )}
            {this.state.isLoggedIn && (
              <>
                <Link to="/aliases" className="text-primary-600 font-medium flex items-center">
                  My Aliases
                  <span className="aliases-indicator" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    marginLeft: '8px',
                    backgroundColor: this.state.aliasesHeldCount > 0 ? '#ef4444' : '#d1d5db',
                    color: this.state.aliasesHeldCount > 0 ? 'white' : '#4b5563'
                  }}>
                    {this.state.aliasesHeldCount || 0}
                  </span>
                </Link>
                <Link to="/mail" className="text-primary-600 font-medium flex items-center">
                  Mail
                  <span className="mail-indicator" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    marginLeft: '8px',
                    backgroundColor: this.state.newMessageCount > 0 ? '#ef4444' : '#d1d5db',
                    color: this.state.newMessageCount > 0 ? 'white' : '#4b5563'
                  }}>
                    {this.state.newMessageCount || 0}
                  </span>
                </Link>
                <Link to="/account" className="text-primary-600 font-medium">Settings</Link>
              </>
            )}
          </div>
          <div className="nav-right">
            {this.state.isLoggedIn ? (
              <div className="flex items-center gap-3">
                <span className="text-gray-600">Hello, {this.state.user?.first_name}</span>
                <button 
                  onClick={() => this.logout()}
                  className="btn btn-ghost btn-sm text-gray-600"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="flex items-center nav-login-buttons">
                <Link to="/" className="btn btn-ghost btn-sm">Sign In</Link>
                <Link to="/new-account" className="btn btn-primary btn-sm">Create Account</Link>
              </div>
            )}
          </div>
        </div>

        <div className="content">

        <Switch>
          <Route path="/forgot-password" component={ForgotPassword} />
          <Route path="/account/reset_password/:auth_id" component={ResetPassword} />
          <Route path="/verify-address/:code" component={VerifyEmail} />

          {secureRoutes}

          <Route path="/privacy" component={Privacy} />
          <Route path="/contact" component={Contact} />


          <Route path="/new-account" component={SignupForm} />

          <Route path="/mail" render={() => {
            return <MailPageLayout />
          }} />

          <Route path="/faq" component={Faq} />

          <Route path="/bookmarklet" component={Bookmarklet} />

          <Route path="/" exact={true} render={() => {
            return this.state.isLoggedIn ? <Redirect to="/aliases" /> : <HomeView />
          }} />
        </Switch>

        </div>

        <div className="footer">
          <div className="footer-links">
            <Link to="/faq">FAQ</Link>&nbsp;
            {this.state.isLoggedIn && (
              <>&middot; <Link to="/bookmarklet">Bookmarklet</Link>&nbsp;</>
            )}
            &middot; <Link to="/privacy">Privacy</Link>&nbsp;
            &middot; <Link to="/contact">Contact</Link>
          </div>
          Copyright &copy; 2006-2025 NslMail.com
        </div>
        </div>
      );
    }

    if (this.state.redirect) {
      body = <Redirect to={this.state.redirect} />
      this.setState({redirect: undefined});
    }

    return (
      <SessionContext.Provider value={this.state}>
      <BrowserRouter>
        {body}
      </BrowserRouter>
      <ToastContainer transition={Slide} />
      </SessionContext.Provider>
    );
  }

}

export default App;
